import React, { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import moment, { Moment } from 'moment'

import { faPencilAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import IProps from './IProps'
import style from './style.scss'

const DateRangeSelection: FunctionComponent<IProps> = ({
    dashboardSettingsValue,
    availableSpans = ['week', 'month'],
    includeFuture = false,
    ...props
}) => {


    const guessRange = (): 'week' | 'month' | 'year' => {
        const diffInDays = props.from.diff(props.to, 'days')

        if (diffInDays >= 364) {
            return 'year'
        }

        if (diffInDays > 7) {
            return 'month'
        }
        return 'week'
    }

    const [edit, setEdit] = useState<boolean>(false)
    const [span, setSpan] = useState<'week' | 'month' | 'year'>(
        props.span ? props.span : guessRange()
    )

    const [dashboardSettings] = useState(dashboardSettingsValue)

    const [from, setFrom] = useState<Moment>(props.from)
    const [to, setTo] = useState<Moment>(props.to)
    const { t } = useTranslation()

    useEffect(() => {
        setSpan(props.span)
        setFrom(props.from)
        setTo(props.to)

    }, [props.span, props.from, props.to])

    const onClick = (
        newFrom: Moment,
        newTo: Moment,
        newSpan: 'week' | 'month' | 'year'
    ) => {
        setFrom(newFrom.clone())
        setTo(newTo.clone())
        setSpan(newSpan)
        setEdit(false)
        props.onSelected(newSpan, newFrom, newTo)
    }

    const getWeeks = () => {
        let weekOptions = []

        const start = moment().subtract(12, 'weeks').startOf('isoWeek')
        if (includeFuture) {
            weekOptions = createWeeks(weekOptions, start, 24)
        } else {
            weekOptions = createWeeks(weekOptions, start, 12)
        }
        return weekOptions
    }

    const createWeeks = (weekOptions) => {
        const currentWeek = moment().isoWeek()
        const currentYear = moment().year()
        let maxWeek = 52
        if (
            moment([currentYear]).isLeapYear() ||
            moment([currentYear - 1]).isLeapYear()
        ) {
            maxWeek = 53
        }
        for (let i = 0; i < maxWeek; i++) {
            const weekStart = moment()
                .isoWeek(currentWeek - i)
                .startOf('isoWeek')
            const weekEnd = weekStart.clone().endOf('isoWeek')
            let weekNumber = currentWeek - i
            if (weekNumber < 1) {
                weekNumber += 52
            }
            if (weekNumber > 52) {
                weekNumber -= 52
            }
            weekOptions.push(
                <button
                    onClick={() => onClick(weekStart, weekEnd, 'week')}
                    className={
                        weekStart.isSame(from, 'day') &&
                        weekEnd.isSame(to, 'day')
                            ? style.active
                            : null
                    }
                    key={'dateRangeWeek' + i}
                >
                    {t('Week {{number}} from {{from}} to {{to}}', {
                        number: weekNumber,
                        from: weekStart.format('DD/MM'),
                        to: weekEnd.format('DD/MM/YYYY'),
                    })}
                </button>
            )
        }
        return weekOptions
    }

    const getMonths = () => {
        let monthOptions = []
        const start = moment().startOf('month')
        if (includeFuture) {
            monthOptions = createMonths(monthOptions, start, 24)
        } else {
            monthOptions = createMonths(monthOptions, start, 12)
        }
        return monthOptions
    }

    const createMonths = (monthOptions, start, number) => {
        for (let i = 0; i < number; i++) {
            const monthStart = start
                .clone()
                .subtract(i, 'months')
                .startOf('month')
            const monthEnd = monthStart.clone().endOf('month')
            monthOptions.push(
                <button
                    onClick={() => onClick(monthStart, monthEnd, 'month')}
                    className={
                        monthStart.isSame(from, 'day') &&
                        monthEnd.isSame(to, 'day')
                            ? style.active
                            : null
                    }
                    key={`dateRangeMonth${i}`}
                >
                    {monthStart.format('MMMM YYYY')}
                </button>
            )
        }

        return monthOptions
    }

    const getYears = () => {
        let yearOptions = []
        const start = moment().startOf('year')
        if (includeFuture) {
            yearOptions = createYears(yearOptions, start, 14)
        } else {
            yearOptions = createYears(yearOptions, start, 7)
        }
        return yearOptions
    }

    const createYears = (yearOptions) => {
        const currentYear = moment().year()
        for (let i = 0; i < 5; i++) {
            const yearStart = moment()
                .year(currentYear - i)
                .startOf('year')
            const yearEnd = yearStart.clone().endOf('year')
            yearOptions.push(
                <button
                    onClick={() => onClick(yearStart, yearEnd, 'year')}
                    className={
                        yearStart.isSame(from, 'day') &&
                        yearEnd.isSame(to, 'day')
                            ? style.active
                            : null
                    }
                    key={`dateRangeYear${i}`}
                >
                    {yearStart.format('YYYY')}
                </button>
            )
        }
        return yearOptions
    }

    const getReadOnlyOption = () => {
        let label

        switch (span) {
            case 'week':
                label = t('Week {{number}} from {{from}} to {{to}}', {
                    number: from.format('W'),
                    from: from.format('DD/MM'),
                    to: to.format('DD/MM/YYYY'),
                })
                break
            case 'month':
                label = from.format('MMMM YYYY')
                break
            case 'year':
                label = from.format('YYYY')
                break
        }
        return <div>{label}</div>
    }

    return (
        <div
            className={`${style.wrapper} ${
                edit ? style.editMode : style.readOnly
            } ${props.showDateFormat ? null : style.compareIcon} ${
                props.zIndex ? style.range1 : null
            }`}
            onClick={() => {
                if (!edit) setEdit(true)
            }}
        >
            {props.children}
            <div className={style.read}>
                {false !== props.showDateFormat ? (
                    <div className={style.span}>
                        {t(
                            span === 'week'
                                ? 'Week'
                                : span === 'year'
                                ? 'Year'
                                : 'Month'
                        )}{' '}
                        |
                    </div>
                ) : null}
                {getReadOnlyOption()}
                <FontAwesomeIcon
                    icon={faPencilAlt}
                    style={{ margin: '0 0.8em' }}
                    className={style.faPencil}
                />
            </div>
            <div className={style.edit}>
                {false !== props.showDateFormat ? (
                    <div
                        className={
                            dashboardSettings === 'dashboard'
                                ? style.dashboardSpan
                                : style.span
                        }
                    >
                        {availableSpans.includes('week') ? (
                            <button
                                onClick={() => (edit ? setSpan('week') : null)}
                                className={
                                    span === 'week' ? style.active : null
                                }
                            >
                                {t('Week')}
                            </button>
                        ) : null}
                        {availableSpans.includes('month') ? (
                            <button
                                onClick={() => (edit ? setSpan('month') : null)}
                                className={
                                    span === 'month' ? style.active : null
                                }
                            >
                                {t('Month')}
                            </button>
                        ) : null}
                        {availableSpans.includes('year') ? (
                            <button
                                onClick={() => (edit ? setSpan('year') : null)}
                                className={
                                    span === 'year' ? style.active : null
                                }
                            >
                                {t('Year')}
                            </button>
                        ) : null}
                    </div>
                ) : null}
                <div className={style.options}>
                    {span === 'week'
                        ? getWeeks()
                        : span === 'month'
                        ? getMonths()
                        : getYears()}
                </div>
            </div>
        </div>
    )
}

export default DateRangeSelection
